// src/pages/gt3x/Results.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const SCHEDULE_RANGE = "Schedule!A1:D";

/**
 * Regeln:
 *  RACE:
 *    FlagURL = P -> in D (Bild)
 *    TeamURL = Q -> in G (Bild)
 *    AutoURL = R -> in I (Bild)
 *    Sichtbar A..L (wir verwenden intern A..M)
 *
 *  QUALY/PRACTICE:
 *    FlagURL = J -> in B (Bild)
 *    AutoURL = K -> in D (Bild)
 *    Sichtbar A..G
 *
 * Zusätze:
 *  - „Start Pos“ -> „Start“
 *  - „Fahrzeugnummer“ -> „#“
 *  - Delta Pos (>0 grün, <0 rot)
 *  - Titel zeigt auch Track-Label aus Schedule
 */

// ---------- Helpers ----------
function colIndexFromLetters(letters){
    let n = 0;
    for (let i=0;i<letters.length;i++) n = n*26 + (letters.charCodeAt(i)-64);
    return n;
}
function letterToIndex(letter, header){
    const num = colIndexFromLetters(letter);
    return num - 1;
}
function colLettersFromIndex(n1){
    let n = n1, out="";
    while(n>0){
        const rem=(n-1)%26;
        out=String.fromCharCode(65+rem)+out;
        n=Math.floor((n-1)/26);
    }
    return out;
}
function lettersBetween(start, end){
    const s = colIndexFromLetters(start);
    const e = colIndexFromLetters(end);
    const list=[];
    for(let i=s;i<=e;i++) list.push(colLettersFromIndex(i));
    return list;
}
function safeVal(row, idx){
    const v = row?.[idx];
    return v === null || v === undefined ? "" : String(v).trim();
}

// Extrahiert eine vorzeichenbehaftete Zahl aus Strings wie "▲2", "+3","-1 ", "2"
function parseSignedIntLike(v){
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    const m = s.match(/[+-]?\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return Number.isNaN(n) ? null : n;
}

const imgFlag = (src) => <img src={src} alt="Flag" className="flag" loading="lazy" />;
const imgTeam = (src) => <img src={src} alt="Team" className="teamlogo" loading="lazy" />;
const imgCar  = (src) => <img src={src} alt="Car"  className="carlogo" loading="lazy" />;

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function isSheetMissingResponse(res){
    if (!res || typeof res !== "object") return true;
    const hasHeader = Array.isArray(res.header) && res.header.length>0;
    const hasRows   = Array.isArray(res.rows)   && res.rows.length>0;
    return !hasHeader && !hasRows;
}

function parseLapTimeToSeconds(v){
    const s = String(v ?? "").trim();
    if (!s) return null;

    // DNS/DNF/BAN ignorieren
    const up = s.toUpperCase();
    if (up === "DNS" || up === "DNF" || up === "BAN") return null;

    // Formate:
    // "1:42.123" => 102.123
    // "42.123"   => 42.123
    const m = s.match(/^(\d+):(\d{2})(?:\.(\d{1,3}))?$/);
    if (m) {
        const min = Number(m[1]);
        const sec = Number(m[2]);
        const ms  = m[3] ? Number(m[3].padEnd(3, "0")) : 0;
        return min * 60 + sec + ms / 1000;
    }

    const f = Number(s.replace(",", "."));
    return Number.isFinite(f) ? f : null;
}


// ---------- View-Builder ----------

function buildRaceView(header, rows){
    const hIndex = (L) => letterToIndex(L, header);

    // ✅ Sichtbare Spalten jetzt A..O (wegen Interval + Pen + Avg + Fastest + Inc)
    const visCols = lettersBetween("A", "N");
    const baseIdx = visCols.map((L) => hIndex(L));

    // ✅ NEUE Bild-Quellen laut deiner Vorgabe
    const idxFlagURL = hIndex("S"); // Flag
    const idxCarURL  = hIndex("T"); // Auto
    const idxTeamURL = hIndex("U"); // Team

    const idxCarNo   = hIndex("E");
    const idxDriver  = hIndex("G");
    const idxTeam    = hIndex("I");

    const posA = hIndex("A");
    const toD  = hIndex("D") - posA; // Flag
    const toE  = hIndex("E") - posA; // #
    const toF  = hIndex("F") - posA; // Auto
    const toG  = hIndex("G") - posA; // Fahrer
    const toH  = hIndex("H") - posA; // Team Bild
    const toI  = hIndex("I") - posA; // Teamname

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    viewHeader[toE] = "#";
    if ((viewHeader[1] || "").toLowerCase() === "start pos") {
        viewHeader[1] = "Start";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[toD] = imgFlag(flagSrc);

        row[toE] = r[idxCarNo];

        const carSrc = safeVal(r, idxCarURL);
        if (carSrc) row[toF] = imgCar(carSrc);

        row[toG] = r[idxDriver];

        const teamSrc = safeVal(r, idxTeamURL);
        if (teamSrc) row[toH] = imgTeam(teamSrc);

        row[toI] = r[idxTeam];

        return row;
    });

    const deltaVisibleIndex = 2;
    return { header: viewHeader, rows: viewRows, deltaVisibleIndex };
}


function buildQualyPracticeView(header, rows){
    const hIndex = (L) => letterToIndex(L, header);
    const visCols = lettersBetween("A","G");       // A..G
    const baseIdx = visCols.map(L => hIndex(L));

    const idxFlagURL = hIndex("J");
    const idxAutoURL = hIndex("K");

    const targetB = hIndex("B");
    const targetD = hIndex("D");

    const viewHeader = visCols.map(L => header[hIndex(L)] ?? L);

    // „Fahrzeugnummer“ -> „#“ auf C
    const visibleCpos = 2; // 0-based
    if ((viewHeader[visibleCpos] || "").toLowerCase().includes("fahrzeugnummer")) {
        viewHeader[visibleCpos] = "#";
    }

    const viewRows = rows.map(r => {
        const row = baseIdx.map(ci => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[targetB - hIndex("A")] = imgFlag(flagSrc); // B

        const carSrc = safeVal(r, idxAutoURL);
        if (carSrc) row[targetD - hIndex("A")] = imgCar(carSrc);    // D

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

// ---------- Konfiguration: Session + Klasse ----------


const cfg = {
    RACE: {
        GESAMT: { fetchRange: (r) => `R${r}_RACE!A1:U100`, buildView: buildRaceView },
        PRO:    { fetchRange: (r) => `R${r}_RACEPRO!A1:U100`, buildView: buildRaceView },
        INT:    { fetchRange: (r) => `R${r}_RACEINT!A1:U100`, buildView: buildRaceView }, // ✅ NEU
        AM:     { fetchRange: (r) => `R${r}_RACEAM!A1:U100`, buildView: buildRaceView },
    },

    QUALY: {
        GESAMT: { fetchRange: (r) => `R${r}_QUALY!A1:K100`, buildView: buildQualyPracticeView },
        PRO:    { fetchRange: (r) => `R${r}_QUALYPRO!A1:K100`, buildView: buildQualyPracticeView },
        INT:    { fetchRange: (r) => `R${r}_QUALYINT!A1:K100`, buildView: buildQualyPracticeView }, // ✅ NEU
        AM:     { fetchRange: (r) => `R${r}_QUALYAM!A1:K100`, buildView: buildQualyPracticeView },
    },

    PRACTICE: {
        GESAMT: { fetchRange: (r) => `R${r}_PRACTICE!A1:K100`, buildView: buildQualyPracticeView },
        PRO:    { fetchRange: (r) => `R${r}_PRACTICEPRO!A1:K100`, buildView: buildQualyPracticeView },
        INT:    { fetchRange: (r) => `R${r}_PRACTICEINT!A1:K100`, buildView: buildQualyPracticeView }, // ✅ NEU
        AM:     { fetchRange: (r) => `R${r}_PRACTICEAM!A1:K100`, buildView: buildQualyPracticeView },
    },
};

export default function Results(){
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);

    const [session, setSession] = useState("RACE");
    const [klass, setKlass] = useState("GESAMT");

    const [viewHeader, setViewHeader] = useState([]);
    const [viewRows, setViewRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // ✅ AUTO-DEFAULT: ResultsFrom (Spalte D) -> mode "results"
    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    fetchSheetViaProxy,
                    SCHEDULE_RANGE,
                    "results"
                );
                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto-race failed (results)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    useEffect(() => {
        (async () => {
            if (!race) return;

            setLoading(true);
            setMsg("");
            setViewHeader([]);
            setViewRows([]);

            try{
                const sessionCfg = cfg[session];
                if (!sessionCfg){
                    setMsg("Ungültige Session-Auswahl.");
                    return;
                }
                const classCfg = sessionCfg[klass];
                if (!classCfg){
                    setMsg("Ungültige Klassen-Auswahl.");
                    return;
                }

                const { fetchRange, buildView } = classCfg;
                const res = await fetchSheetViaProxy({ range: fetchRange(race) });

                if (isSheetMissingResponse(res)) {
                    setMsg("Rennen hat noch nicht stattgefunden oder wurde noch nicht ausgewertet.");
                    return;
                }

                const header = Array.isArray(res?.header) ? res.header : [];
                const rows   = Array.isArray(res?.rows) ? res.rows.map(r => header.map(h => r[h])) : [];

                const { header: vHeader, rows: vRows } = buildView(header, rows);
                setViewHeader(vHeader);
                setViewRows(vRows);
            }catch(e){
                console.warn("Results load failed", e);
                setMsg("Rennen hat noch nicht stattgefunden oder wurde noch nicht ausgewertet.");
            }finally{
                setLoading(false);
            }
        })();
    }, [race, session, klass]);

    // Spaltenindizes für Styling (Delta Pos färben)
    const colIdxMap = useMemo(() => {
        const map = new Map();
        viewHeader.forEach((h,i) => map.set((h||"").toLowerCase(), i));
        return map;
    }, [viewHeader]);

    // RACE: Δ POS ist sicher die 3. sichtbare Spalte (C) -> 0-based = 2
    const deltaCol = useMemo(() => {
        if (session === "RACE") return 2;
        return colIdxMap.get("delta pos");
    }, [session, colIdxMap]);

    const numericCols = useMemo(() => {
        const set = new Set();
        viewRows.forEach(row =>
            row.forEach((cell,i) => { if (isNumeric(cell)) set.add(i); })
        );
        return set;
    }, [viewRows]);

    // ✅ Fastest Lap Spalte finden (pro Tabelle: Gesamt / PRO / AM)
    const fastestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex(h =>
            String(h ?? "").toLowerCase().includes("fastest")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

// ✅ schnellste Fastest Lap der aktuellen Tabelle bestimmen
    const fastestLapBest = useMemo(() => {
        if (fastestLapCol === null) return null;

        let best = null;
        for (const row of viewRows) {
            const t = parseLapTimeToSeconds(row?.[fastestLapCol]);
            if (t === null) continue;
            if (best === null || t < best) best = t;
        }
        return best;
    }, [viewRows, fastestLapCol]);

    const avgLapCol = useMemo(() => {
        const idx = viewHeader.findIndex(h => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("avg lap") || s.includes("average lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const bestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex(h => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("best lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const pillForKlass = (k) => {
        const key = String(k || "GESAMT").toUpperCase();
        const cls =
            key === "PRO" ? "pill--pro" :
                key === "AM"  ? "pill--am"  :
                    key === "INT" ? "pill--int" :
                        "pill--gesamt";

        return <span className={`pill ${cls}`}>{key}</span>;
    };

    return (
        <div className="rv-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">GT3 Xperience</div>
                        <h1 className="rv-page-title">Ergebnisse</h1>
                        <p className="rv-page-subtitle results-subtitle">
                            {session} {pillForKlass(klass)}
                        </p>
                    </div>
                </div>
            </section>

            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                autoSelectFirst={false}
                valueSession={session}
                onSessionChange={(val) => {
                    setSession(val);
                    setKlass("GESAMT");
                }}
                showSession={true}
                valueClass={klass}
                onClassChange={setKlass}
                showClass={true}
            />
            {msg && (
                <div className="card">
                    <p className="text-dim">{msg}</p>
                </div>
            )}
            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !msg && (
                <div className="fancyTable">
                    <table className="tbl">
                        <thead>
                        <tr>
                            {viewHeader.map((h,i) => {
                                const isAvg  = avgLapCol !== null && i === avgLapCol;
                                const isBest = bestLapCol !== null && i === bestLapCol;

                                return (
                                    <th key={i} className={(isAvg || isBest) ? "lap-center" : ""}>
                                        {h}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {viewRows.map((r,ri) => (
                            <tr key={ri}>
                                {r.map((cell,ci) => {
                                    let cls = "";

                                    if (numericCols.has(ci)) cls = "num";

                                    if (typeof deltaCol === "number" && ci === deltaCol) {
                                        const n = parseSignedIntLike(cell);
                                        if (n !== null) {
                                            if (n > 0) cls = `${cls} delta-up`.trim();
                                            else if (n < 0) cls = `${cls} delta-down`.trim();
                                        }
                                    }

                                    // ✅ NEU: Penalty-Spalten automatisch rot färben
                                    const headerName = (viewHeader?.[ci] || "").toLowerCase();
                                    if (headerName.includes("pen") && String(cell).trim() !== "") {
                                        cls = `${cls} penalty-red`.trim();
                                    }

                                    // ✅ Best Fastest Lap purple markieren
                                    if (fastestLapCol !== null && fastestLapBest !== null && ci === fastestLapCol) {
                                        const t = parseLapTimeToSeconds(cell);
                                        // kleine Toleranz gegen Float-Fehler
                                        if (t !== null && Math.abs(t - fastestLapBest) < 0.0005) {
                                            cls = `${cls} fastest-best`.trim();
                                        }
                                    }

                                    if (avgLapCol !== null && ci === avgLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }
                                    if (bestLapCol !== null && ci === bestLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }

                                    return (
                                        <td key={ci} className={cls}>
                                            {cell}
                                        </td>
                                    );
                                })}

                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
